import React, { useEffect, useState, useContext } from "react";
import { useAuth } from "../hooks/AuthProvider";
import { useNavigate } from "react-router-dom";
import "./Login.css";
import { LanguageContext } from "../context/LanguageContext";
import authTranslations from "../languages/auth.json";
import Logo from "../assets/image/logoo.png";
import { useDialog } from "../Components/DialogProvider";

const EyeIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
);

const EyeOffIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M1 1l22 22"></path>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"></path>
  </svg>
);

const LOGIN_DRAFT_EMAIL_KEY = "login_draft_email";
const LOGIN_DRAFT_PASSWORD_KEY = "login_draft_password";

export const Login = () => {
  const { lang } = useContext(LanguageContext);
  const t = authTranslations[lang].Login;
  const isRtl = ["ar", "ur"].includes(lang);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const [showPassword, setShowPassword] = useState(false);

  const { alert } = useDialog();
  const auth = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const savedEmail = sessionStorage.getItem(LOGIN_DRAFT_EMAIL_KEY) || "";
    const savedPassword = sessionStorage.getItem(LOGIN_DRAFT_PASSWORD_KEY) || "";
    if (savedEmail) setEmail(savedEmail);
    if (savedPassword) setPassword(savedPassword);
  }, []);

  const onEmailChange = (val) => {
    setEmail(val);
    sessionStorage.setItem(LOGIN_DRAFT_EMAIL_KEY, val);
  };

  const onPasswordChange = (val) => {
    setPassword(val);
    sessionStorage.setItem(LOGIN_DRAFT_PASSWORD_KEY, val);
  };

  const onFormSubmit = async (e) => {
    e.preventDefault();

    setEmailError("");
    setPasswordError("");

    const trimmedEmail = (email || "").trim();

    if (!trimmedEmail) {
      setEmailError(t.errorRequiredEmail);
      return;
    }

    if (password && password.length < 8) {
      setPasswordError(t.errorPasswordLength);
      return;
    }

    try {
      setSubmitting(true);

      await auth.loginAction(trimmedEmail, password, alert);

      sessionStorage.removeItem(LOGIN_DRAFT_EMAIL_KEY);
      sessionStorage.removeItem(LOGIN_DRAFT_PASSWORD_KEY);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="split-screen-container" dir={isRtl ? "rtl" : "ltr"}>
      <div className="left-pane">
        <div className="brand-content">
          <div className="logo-area">
            <img src={Logo} alt="Sama-IOT Logo" />
            <span className="brand-name">Sama-IOT</span>
          </div>
          <div className="hero-text">
            <h1>{t.heroTitle}</h1>
            <p>{t.heroDesc}</p>
          </div>
        </div>
        <div className="bg-gradient-overlay"></div>
      </div>

      <div className="right-pane">
        <div className="login-card">
          <div className="card-header">
            <h2>{t.title}</h2>
            <p className="subtitle">{t.subtitle}</p>
          </div>

          <form onSubmit={onFormSubmit}>
            <div className="form-group">
              <label htmlFor="user-email">{t.labelEmail}</label>
              <div className={`input-wrapper ${emailError ? "has-error" : ""}`}>
                <input
                  id="user-email"
                  type="email"
                  value={email}
                  placeholder={t.placeholderEmail}
                  onChange={(ev) => onEmailChange(ev.target.value)}
                  className="form-input"
                  autoComplete="off"
                />
              </div>
              {emailError && <span className="error-message">{emailError}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="password">{t.labelPassword}</label>
              <div
                className={`input-wrapper ${passwordError ? "has-error" : ""}`}
                style={{ position: "relative" }}
              >
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  placeholder={t.placeholderPassword}
                  onChange={(ev) => onPasswordChange(ev.target.value)}
                  className="form-input"
                  autoComplete="current-password"
                  style={{
                    paddingLeft: isRtl ? "45px" : "15px",
                    paddingRight: isRtl ? "15px" : "45px",
                    textAlign: isRtl ? "right" : "left",
                  }}
                />
                <button
                  type="button"
                  className="eye-toggle"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  style={{
                    position: "absolute",
                    top: "50%",
                    transform: "translateY(-50%)",
                    [isRtl ? "left" : "right"]: "12px",
                    [isRtl ? "right" : "left"]: "auto",
                    background: "none",
                    border: "none",
                    padding: "0",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    zIndex: 10,
                  }}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
              {passwordError && (
                <span className="error-message">{passwordError}</span>
              )}
            </div>

            <div className="form-actions">
              <label className="checkbox-container">
              </label>
              <button
                type="button"
                onClick={() => navigate("/forgot-password")}
                className="forgot-password-link"
              >
                {t.forgotPasswordLink}
              </button>
            </div>

            <button className="submit-btn" type="submit" disabled={submitting}>
              {submitting ? t.signingIn : t.buttonLogin}
            </button>

            <div className="divider">
              <span>{isRtl ? "أو" : "or"}</span>
            </div>

            <div className="signup-footer">
              <span>{t.noAccount} </span>
              <button
                className="create-account-link"
                onClick={() => navigate("/contact-us")}
                type="button"
              >
                {t.buttonSignUp}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
